<script setup>
  import { computed } from 'vue'

  /**
   * BcstQuizQuestionCard — presentational quiz question card.
   *
   * Renders a single question with its options and navigation.
   * Contains no business logic, Firebase queries, or persistence.
   * All state is received via props; all interactions emitted to parent.
   *
   * Supports single-answer (radio) and multiple-answer (checkbox) questions,
   * driven by question.type === 'multiple_choice'. `selected` is a scalar for
   * single-answer questions and an array of option ids for multiple-answer ones.
   */
  import BcstLessonBadge from './BcstLessonBadge.vue'

  const props = defineProps({
    question: { type: Object, required: true },   // { id, text, type, options: [{id, text}] }
    questionIndex: { type: Number, required: true },
    totalQuestions: { type: Number, required: true },
    selected: { type: [String, Number, Array], default: null },
    isFirst: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    allAnswered: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
    // An answer auto-save is in flight: the nav button shows "Saving..." + spinner and is
    // disabled so the user can't advance until the selection is persisted.
    isAutoSaving: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // Read-only review (Results Breakdown): hides the nav footer and, instead of every
    // option, shows only the correct answer(s) and the user's own pick(s), each badged
    // Correct/Incorrect.
    reviewMode: { type: Boolean, default: false },
    // The question's correct option id(s), from the server. Only used in review mode.
    correctOptionIds: { type: Array, default: () => [] },
  })

  const emit = defineEmits(['select', 'previous', 'next', 'submit'])

  const isMulti = computed(() => props.question?.type === 'multiple_choice')

  function isOptionSelected(optionId) {
    if (Array.isArray(props.selected)) return props.selected.map(String).includes(String(optionId))
    return String(props.selected) === String(optionId)
  }

  function isCorrectOption(optionId) {
    return props.correctOptionIds.map(String).includes(String(optionId))
  }

  // Every option, each with its letter (A/B/C… by position). Review mode renders the full
  // list too and marks them: "Correct" on the correct answer(s), "Your Answer" on the
  // user's pick(s); the rest stay neutral.
  const displayOptions = computed(() =>
    (props.question?.options ?? []).map((option, i) => ({ ...option, letter: LETTER(i) }))
  )

  // Whether the current question has at least one selection (gates the Next button).
  const hasSelection = computed(() =>
    Array.isArray(props.selected) ? props.selected.length > 0 : props.selected != null
  )

  const LETTER = (i) => String.fromCharCode(65 + i)
</script>

<template>
  <div class="qqc-card" role="group" :aria-label="`Question ${questionIndex + 1} of ${totalQuestions}`">

    <!-- ── Header ── -->
    <div class="qqc-header">
      <span class="qqc-counter">Question {{ questionIndex + 1 }} of {{ totalQuestions }}</span>
    </div>
    <hr class="qqc-divider" />

    <!-- ── Question + Options ── -->
    <div class="qqc-body">
      <p class="qqc-question-text">{{ question.text }}</p>
      <p v-if="isMulti" class="qqc-multi-hint">Select all that apply.</p>

      <div
        :role="isMulti ? 'group' : 'radiogroup'"
        :aria-label="question.text">
        <label
          v-for="option in displayOptions"
          :key="option.id"
          class="qqc-option"
          :class="{ 'qqc-option--selected': isOptionSelected(option.id), 'qqc-option--review': reviewMode }">
          <input
            :type="isMulti ? 'checkbox' : 'radio'"
            class="visually-hidden"
            :name="`quiz-q-${question.id}`"
            :value="option.id"
            :checked="isOptionSelected(option.id)"
            :disabled="disabled || reviewMode"
            @change="emit('select', option.id)" />
          <span
            class="qqc-indicator"
            :class="{ 'qqc-indicator--selected': isOptionSelected(option.id) }"
            aria-hidden="true">
            {{ option.letter }}
          </span>
          <span
            class="qqc-option-text"
            :class="{ 'qqc-option-text--selected': isOptionSelected(option.id) }">
            {{ option.text }}
            <!-- The user's own pick: italic label sitting right after the answer text. -->
            <span v-if="reviewMode && isOptionSelected(option.id)" class="qqc-option-your-answer">(Your Answer)</span>
          </span>
          <!-- The correct answer(s): badge pinned to the right of the row. -->
          <BcstLessonBadge
            v-if="reviewMode && isCorrectOption(option.id)"
            class="qqc-option-result-badge"
            variant="success">
            Correct Answer
          </BcstLessonBadge>
          <!-- The user's pick when it is NOT a correct option: flagged as a wrong answer so
               a wrong pick is never shown as merely "(Your Answer)" with no verdict. -->
          <BcstLessonBadge
            v-else-if="reviewMode && isOptionSelected(option.id) && !isCorrectOption(option.id)"
            class="qqc-option-result-badge"
            variant="danger">
            Wrong Answer
          </BcstLessonBadge>
        </label>
      </div>
    </div>

    <template v-if="!reviewMode">
    <hr class="qqc-divider" />

    <!-- ── Navigation ── -->
    <div class="qqc-nav">
      <button
        type="button"
        class="qqc-btn qqc-btn--secondary"
        :disabled="isFirst || disabled || isAutoSaving"
        @click="emit('previous')">
        <i class="bi bi-chevron-left" aria-hidden="true"></i>
        Previous Question
      </button>

      <button
        v-if="isLast"
        type="button"
        class="qqc-btn qqc-btn--primary"
        :disabled="!allAnswered || isSaving || disabled || isAutoSaving"
        @click="emit('submit')">
        {{ isAutoSaving ? 'Saving...' : (allAnswered ? 'Submit and Get Your Score' : 'Answer all questions to submit') }}
        <i v-if="!isAutoSaving && allAnswered" class="bi bi-chevron-right" aria-hidden="true"></i>
      </button>

      <button
        v-else
        type="button"
        class="qqc-btn qqc-btn--primary"
        :disabled="!hasSelection || disabled || isAutoSaving"
        @click="emit('next')">
        {{ isAutoSaving ? 'Saving...' : 'Submit and Go to Next Question' }}
        <i v-if="!isAutoSaving" class="bi bi-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
    </template>

  </div>
</template>

<style scoped>
  /* ── Card ── */
  .qqc-card {
    border: 1px solid #c5cdd4;
    border-radius: 4px;
    background: #fff;
    overflow: hidden;
  }

  /* ── Header ── */
  .qqc-header {
    background: #f4f4f5;
    padding: 13px 27px;
  }

  .qqc-counter {
    font-size: 20px;
    font-weight: 700;
    color: #212529;
    letter-spacing: -0.4px;
    line-height: 1.25;
  }

  /* ── Divider ── */
  .qqc-divider {
    border: none;
    border-top: 1px solid #c5cdd4;
    margin: 0;
  }

  /* ── Body ── */
  .qqc-body {
    padding-top: 28px;
  }

  .qqc-question-text {
    font-size: 18px;
    font-weight: 700;
    color: #212529;
    letter-spacing: -0.36px;
    line-height: 1.3;
    margin: 0 27px 24px;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  .qqc-multi-hint {
    font-size: 13px;
    font-weight: 500;
    color: #6c757d;
    margin: -16px 27px 20px;
  }

  /* ── Options ── */
  .qqc-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 17px 27px;
    cursor: pointer;
    width: 100%;
    margin: 0;
    transition: background-color 0.15s ease;
  }

  .qqc-option:not(.qqc-option--review):hover {
    background-color: #f7f1fd;
  }

  .qqc-option--selected {
    background-color: #f7f1fd;
  }

  /* ── Review mode: identical option styling to the player (selected = eggplant); the
     ONLY addition is a Correct/Incorrect badge on the option the user selected. ── */
  .qqc-option--review {
    cursor: default;
  }

  /* The user's own pick — italic label attached to the answer text with a discreet gap. */
  .qqc-option-your-answer {
    margin-left: 8px;
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    color: var(--bs-gray-600, #6c757d);
    white-space: nowrap;
  }

  /* The result badge ("Correct Answer" / "Wrong Answer") is pinned to the right edge. */
  .qqc-option-result-badge {
    margin-left: auto;
    flex-shrink: 0;
  }

  /* ── Indicator ── */
  .qqc-indicator {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #5c0cb8;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    color: #5c0cb8;
    background: #fff;
    line-height: 1;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .qqc-indicator--selected {
    background: #5c0cb8;
    color: #fff;
  }

  /* ── Option text ── */
  .qqc-option-text {
    font-size: 16px;
    font-weight: 500;
    color: #495057;
    letter-spacing: -0.32px;
    line-height: 1.3;
    white-space: normal;
    overflow: visible;
  }

  .qqc-option-text--selected {
    font-weight: 700;
  }

  /* ── Navigation ── */
  .qqc-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 20px 27px;
  }

  /* ── Buttons ── */
  .qqc-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 13px;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    cursor: pointer;
    border: 1px solid;
    white-space: nowrap;
    transition: opacity 0.15s ease;
  }

  .qqc-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .qqc-btn--secondary {
    background: #fff;
    border-color: #5c0cb8;
    color: #5c0cb8;
  }

  .qqc-btn--primary {
    background: #5c0cb8;
    border-color: #5c0cb8;
    color: #fff;
  }

  /* Match the Retake button: a disabled primary turns SOLID gray with white text — not a
     faded purple. Uses the theme's button-disabled token (with the same #6c757d fallback,
     since --bs-btn-disabled-bg is scoped to .btn-primary and not in this custom button). */
  .qqc-btn--primary:disabled {
    background: var(--bs-btn-disabled-bg, #6c757d);
    border-color: var(--bs-btn-disabled-border-color, #6c757d);
    color: var(--bs-btn-disabled-color, #fff);
    opacity: var(--bs-btn-disabled-opacity, 0.65);
  }
</style>
